import * as Api from '@ViewModels';
import * as React from 'react';
import { IContentCalendarSelectedSuggestion } from '../../../../../../../models';
import { getUniqueIdForSuggestion } from '../../../../../../../models/UiUtils';
import {
	extractTagNamesFromContactFilter,
	isTagCriterion,
	normalizeAudienceCriteria,
} from '../../../../../../../models/utils/contentCalendarAudienceUtils';

export type AudienceMode = 'same' | 'per';

export interface ICampaignAudience {
	criteria: Api.IContactFilterCriteria[];
	suggestedTagNames: string[];
	ownershipFilter: Api.IOwnershipFilter | null;
	groupByHousehold: boolean;
	excludedContacts: Api.IContact[];
}

export function getTagChips(audience: ICampaignAudience): Array<{ name: string; suggested: boolean }> {
	return audience.criteria
		.filter(criterion => isTagCriterion(criterion) && !!criterion.value)
		.map(criterion => ({
			name: criterion.value as string,
			suggested: audience.suggestedTagNames.includes(criterion.value as string),
		}));
}

export function getAdvancedCriteriaCount(audience: ICampaignAudience): number {
	return audience.criteria.length - getTagChips(audience).length;
}

export function isAudienceEmpty(audience: ICampaignAudience | undefined): boolean {
	return !audience || audience.criteria.length === 0;
}

const seedAudiences = (suggestions: IContentCalendarSelectedSuggestion[]): Record<string, ICampaignAudience> => {
	const audiences: Record<string, ICampaignAudience> = {};
	suggestions.forEach(suggestion => {
		const tagNames = extractTagNamesFromContactFilter(suggestion.filter);
		audiences[getUniqueIdForSuggestion(suggestion)] = {
			criteria: tagNames.map(value => ({
				op: Api.FilterOperator.Or,
				property: Api.ContactFilterCriteriaProperty.Tag,
				value,
			})),
			suggestedTagNames: tagNames,
			ownershipFilter: null,
			groupByHousehold: true,
			excludedContacts: [],
		};
	});
	return audiences;
};

export function useCampaignAudiences(suggestions: IContentCalendarSelectedSuggestion[]) {
	const [audienceMode, setAudienceMode] = React.useState<AudienceMode>('same');
	const [audiences, setAudiences] = React.useState<Record<string, ICampaignAudience>>(() => seedAudiences(suggestions));
	const [isDirty, setIsDirty] = React.useState(false);

	const hasAiSuggestedTags = React.useMemo(
		() => suggestions.some(suggestion => extractTagNamesFromContactFilter(suggestion.filter).length > 0),
		[suggestions]
	);

	const updateAudience = (campaignId: string, updater: (audience: ICampaignAudience) => ICampaignAudience) => {
		setAudiences(value => {
			const audience = value[campaignId];
			if (!audience) {
				return value;
			}
			return { ...value, [campaignId]: updater(audience) };
		});
		setIsDirty(true);
	};

	const removeTag = (campaignId: string, name: string) =>
		updateAudience(campaignId, audience => ({
			...audience,
			criteria: audience.criteria.filter(criterion => !(isTagCriterion(criterion) && criterion.value === name)),
			suggestedTagNames: audience.suggestedTagNames.filter(tagName => tagName !== name),
		}));

	const addTag = (campaignId: string, name: string) =>
		updateAudience(campaignId, audience =>
			audience.criteria.some(criterion => isTagCriterion(criterion) && criterion.value === name)
				? audience
				: {
						...audience,
						criteria: [
							...audience.criteria,
							{ op: Api.FilterOperator.Or, property: Api.ContactFilterCriteriaProperty.Tag, value: name },
						],
					}
		);

	const applyFlyoutEdits = (campaignId: string, audience: ICampaignAudience) =>
		updateAudience(campaignId, () => ({ ...audience, criteria: normalizeAudienceCriteria(audience.criteria) }));

	const resetAudiences = () => {
		setAudiences(seedAudiences(suggestions));
		setIsDirty(false);
	};

	return {
		audienceMode,
		setAudienceMode,
		hasAiSuggestedTags,
		audiences,
		removeTag,
		addTag,
		applyFlyoutEdits,
		resetAudiences,
		isDirty,
	};
}
